import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Play, Pause, Check, Edit3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SAMPLER_OPTIONS = [
  { value: 'Naeem', label: 'Naeem' },
  { value: 'Burhan', label: 'Burhan' },
  { value: 'Arsalan', label: 'Arsalan' }
];

export default function SamplingTimeTracker({ user }) {
  const navigate = useNavigate();
  const { hasPermission, canCreate, canEdit, canDelete } = usePermissions(user);
  const [entries, setEntries] = useState([]);
  const [samplingArticles, setSamplingArticles] = useState([]);
  const [articlesData, setArticlesData] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState('');
  const [selectedArticleCategory, setSelectedArticleCategory] = useState('');
  const [selectedSampler, setSelectedSampler] = useState('');
  const [activeEntries, setActiveEntries] = useState([]);
  const [runningTimers, setRunningTimers] = useState({});
  const timerIntervals = useRef({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [manualEntryAuthOpen, setManualEntryAuthOpen] = useState(false);
  const [manualTimeDialogOpen, setManualTimeDialogOpen] = useState(false);
  const [entryForManual, setEntryForManual] = useState(null);
  const [manualMinutes, setManualMinutes] = useState('');

  useEffect(() => {
    fetchEntries();
    fetchSamplingArticles();
  }, []);

  useEffect(() => {
    // Set up timers for all running entries - calculate from server time
    activeEntries.forEach(entry => {
      if (entry.status === 'RUNNING' && !timerIntervals.current[entry.id]) {
        // Calculate initial elapsed time from server
        const calculateElapsedTime = () => {
          if (entry.last_action_time) {
            const lastActionDate = new Date(entry.last_action_time);
            const now = new Date();
            const elapsedSinceLastAction = Math.floor((now - lastActionDate) / 1000);
            return entry.total_time_seconds + elapsedSinceLastAction;
          }
          return entry.total_time_seconds;
        };

        // Update timer every second based on server time
        timerIntervals.current[entry.id] = setInterval(() => {
          setRunningTimers(prev => ({
            ...prev,
            [entry.id]: calculateElapsedTime()
          }));
        }, 1000);
        
        // Set initial time
        setRunningTimers(prev => ({
          ...prev,
          [entry.id]: calculateElapsedTime()
        }));
      } else if (entry.status !== 'RUNNING' && timerIntervals.current[entry.id]) {
        clearInterval(timerIntervals.current[entry.id]);
        delete timerIntervals.current[entry.id];
      }
    });

    // Cleanup intervals for entries no longer active
    Object.keys(timerIntervals.current).forEach(entryId => {
      if (!activeEntries.find(e => e.id === entryId && e.status === 'RUNNING')) {
        clearInterval(timerIntervals.current[entryId]);
        delete timerIntervals.current[entryId];
      }
    });

    return () => {
      Object.values(timerIntervals.current).forEach(interval => clearInterval(interval));
      timerIntervals.current = {};
    };
  }, [activeEntries]);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sampling-time-tracker`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
      
      // Find all active entries
      const active = response.data.filter(e => e.status !== 'COMPLETED');
      setActiveEntries(active);
      
      // Initialize running timers with calculated elapsed time from server
      const timers = {};
      active.forEach(entry => {
        if (entry.status === 'RUNNING' && entry.last_action_time) {
          const lastActionDate = new Date(entry.last_action_time);
          const now = new Date();
          const elapsedSinceLastAction = Math.floor((now - lastActionDate) / 1000);
          timers[entry.id] = entry.total_time_seconds + elapsedSinceLastAction;
        } else {
          timers[entry.id] = entry.total_time_seconds;
        }
      });
      setRunningTimers(timers);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
      toast.error('Failed to fetch entries');
    }
  };

  // Auto-refresh every 5 seconds, but pause when any dialog is open
  const isUserEditing = deleteDialogOpen || authDialogOpen || manualTimeDialogOpen || 
                        formOpen || manualEntryAuthOpen;
  useAutoRefresh(fetchEntries, 5, isUserEditing);

  const fetchSamplingArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/designs/sampling-articles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store full article data
      const articlesMap = {};
      response.data.forEach(article => {
        articlesMap[article.article_code] = article;
      });
      setArticlesData(articlesMap);
      
      setSamplingArticles(response.data.map(a => ({
        value: a.article_code,
        label: a.article_code
      })));
    } catch (error) {
      console.error('Failed to fetch sampling articles:', error);
    }
  };

  const handleArticleSelect = (articleCode) => {
    setSelectedArticle(articleCode);
    const articleData = articlesData[articleCode];
    if (articleData) {
      setSelectedArticleCategory(articleData.category || 'N/A');
    }
  };

  const handleCreateEntry = async () => {
    if (!selectedArticle || !selectedSampler) {
      toast.error('Please select both article and sampler');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/sampling-time-tracker`,
        {
          article_code: selectedArticle,
          sampler_name: selectedSampler
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Entry created successfully');
      setFormOpen(false);
      setSelectedArticle('');
      setSelectedArticleCategory('');
      setSelectedSampler('');
      fetchEntries();
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    }
  };

  const handleStart = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/sampling-time-tracker/${entryId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Timer started');
      fetchEntries();
    } catch (error) {
      console.error('Error starting timer:', error);
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    }
  };

  const handlePause = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/sampling-time-tracker/${entryId}/pause`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Timer paused');
      fetchEntries();
    } catch (error) {
      console.error('Error pausing timer:', error);
      toast.error(error.response?.data?.detail || 'Failed to pause timer');
    }
  };

  const handleComplete = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/sampling-time-tracker/${entryId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Entry completed and saved');
      fetchEntries();
    } catch (error) {
      console.error('Error completing timer:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete timer');
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!entryToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/sampling-time-tracker/${entryToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Entry deleted successfully');
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
      setDeleteDialogOpen(false);
    }
  };

  const requestManualEntry = (entry) => {
    setEntryForManual(entry);
    setManualEntryAuthOpen(true);
  };

  const handleManualEntryAuthorized = () => {
    setManualEntryAuthOpen(false);
    setManualTimeDialogOpen(true);
  };

  const handleSaveManualTime = async () => {
    if (!entryForManual || !manualMinutes) {
      toast.error('Please enter time in minutes');
      return;
    }

    const minutes = parseInt(manualMinutes);
    if (isNaN(minutes) || minutes <= 0) {
      toast.error('Please enter a valid positive number');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const totalSeconds = minutes * 60;
      
      // Update the entry with manual time and mark as completed
      await axios.patch(
        `${API}/sampling-time-tracker/${entryForManual.id}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update the total time directly in the database
      await axios.patch(
        `${API}/sampling-time-tracker/${entryForManual.id}/manual-time`,
        { total_time_seconds: totalSeconds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Manual time entry saved: ${minutes} minutes`);
      setManualTimeDialogOpen(false);
      setManualMinutes('');
      setEntryForManual(null);
      fetchEntries();
    } catch (error) {
      console.error('Error saving manual time:', error);
      toast.error(error.response?.data?.detail || 'Failed to save manual time');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatMinutesOnly = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Sampling Time Tracker
              </h1>
              <p className="text-sm text-gray-600">Track sampling time for multiple articles</p>
            </div>
          </div>
          {canCreate('sampling_time_tracker') && (
            <Button onClick={() => setFormOpen(true)} className="gap-2 bg-black hover:bg-gray-800">
              <Plus className="w-4 h-4" />
              New Entry
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Active Entries */}
        {activeEntries.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold mb-4 uppercase tracking-tight">Active Tracking</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeEntries.map(entry => {
                const currentTime = runningTimers[entry.id] || entry.total_time_seconds;
                return (
                  <div key={entry.id} className="bg-white border-2 border-black rounded-lg p-4">
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500">ARTICLE</p>
                      <p className="text-lg font-bold">{entry.article_code}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500">SAMPLER</p>
                      <p className="font-medium">{entry.sampler_name}</p>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500">TIME ELAPSED</p>
                      <p className="text-2xl font-bold text-blue-600">{formatTime(currentTime)}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        {(entry.status === 'CREATED' || entry.status === 'PAUSED') && (
                          <Button
                            onClick={() => handleStart(entry.id)}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            {entry.status === 'CREATED' ? 'Start' : 'Resume'}
                          </Button>
                        )}
                        {entry.status === 'RUNNING' && (
                          <Button
                            onClick={() => handlePause(entry.id)}
                            className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
                            size="sm"
                          >
                            <Pause className="w-4 h-4 mr-1" />
                            Pause
                          </Button>
                        )}
                        {entry.status !== 'CREATED' && (
                          <Button
                            onClick={() => handleComplete(entry.id)}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                            size="sm"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                        )}
                      </div>
                      {entry.status === 'CREATED' && (
                        <Button
                          onClick={() => requestManualEntry(entry)}
                          variant="outline"
                          size="sm"
                          className="w-full border-2 border-gray-400 hover:border-gray-600"
                        >
                          <Edit3 className="w-4 h-4 mr-1" />
                          Enter Time Manually
                        </Button>
                      )}
                    </div>
                    <div className="mt-2">
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                        entry.status === 'RUNNING' ? 'bg-green-100 text-green-800' :
                        entry.status === 'PAUSED' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {entry.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All Entries List */}
        <div>
          <h2 className="text-lg font-bold mb-4 uppercase tracking-tight">All Entries</h2>
          {entries.length === 0 ? (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-500">No entries yet. Create your first sampling time entry.</p>
            </div>
          ) : (
            <div className="bg-white border-2 border-black rounded-lg overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b-2 border-black">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Article</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Sampler</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Total Time</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {entries.map(entry => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{entry.article_code}</td>
                      <td className="px-4 py-3">{entry.sampler_name}</td>
                      <td className="px-4 py-3 font-bold text-blue-600">{formatTime(entry.total_time_seconds)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                          entry.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          entry.status === 'RUNNING' ? 'bg-blue-100 text-blue-800' :
                          entry.status === 'PAUSED' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {canDelete('sampling_time_tracker') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(entry)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Create Entry Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">New Sampling Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Article Code (LN-X)</Label>
              <SearchableSelect
                options={samplingArticles}
                value={selectedArticle}
                onChange={handleArticleSelect}
                placeholder="Select article..."
                className="mt-1"
              />
            </div>
            
            {selectedArticleCategory && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Article Category</Label>
                <div className="mt-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md">
                  <p className="text-sm font-medium">{selectedArticleCategory}</p>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Sampler Name</Label>
              <SearchableSelect
                options={SAMPLER_OPTIONS}
                value={selectedSampler}
                onChange={setSelectedSampler}
                placeholder="Select sampler..."
                className="mt-1"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => setFormOpen(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleCreateEntry} className="flex-1 bg-black hover:bg-gray-800">
                Create Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {/* Delete Authorization Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={confirmDelete}
        title="Delete Sampling Entry"
        message={entryToDelete ? `Enter authorization code to delete sampling entry for ${entryToDelete.article_code} by ${entryToDelete.sampler_name}` : 'Enter authorization code to proceed'}
      />

      {/* Manual Entry Authorization Dialog */}
      <Dialog open={manualEntryAuthOpen} onOpenChange={setManualEntryAuthOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">Manual Entry Authorization</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            const code = e.target.code.value;
            if (code === 'samdata') {
              handleManualEntryAuthorized();
              e.target.code.value = '';
            } else {
              toast.error('Invalid authorization code');
              e.target.code.value = '';
            }
          }} className="space-y-4">
            <div className="p-4 border-2 border-yellow-500 bg-yellow-50">
              <p className="text-sm font-medium text-yellow-800">Enter authorization code to manually input time</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-gray-500">AUTHORIZATION CODE</Label>
              <input
                type="password"
                name="code"
                placeholder="Enter code"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setManualEntryAuthOpen(false);
                  setEntryForManual(null);
                }}
                className="flex-1 border-2 border-gray-300"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-black hover:bg-gray-800"
              >
                Authorize
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manual Time Entry Dialog */}
      <Dialog open={manualTimeDialogOpen} onOpenChange={setManualTimeDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">Enter Time Manually</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 border-2 border-blue-500 bg-blue-50">
              <p className="text-sm font-medium text-blue-800">
                Entry: {entryForManual?.article_code} - {entryForManual?.sampler_name}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Time (in minutes)</Label>
              <input
                type="number"
                value={manualMinutes}
                onChange={(e) => setManualMinutes(e.target.value)}
                placeholder="Enter minutes"
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                autoFocus
              />
              <p className="text-xs text-gray-500">This will save the entry as completed with the specified time</p>
            </div>

            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setManualTimeDialogOpen(false);
                  setManualMinutes('');
                  setEntryForManual(null);
                }}
                className="flex-1 border-2 border-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveManualTime}
                className="flex-1 bg-black hover:bg-gray-800"
              >
                Save Time
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
